/**
 * The template libraries - what kinds of template exist, in one list.
 *
 * They are unrelated to each other, and the only thing they share is the page
 * they live on: `/templates?type=<id>`. That id was spelled out as a string
 * literal in the tab strip, in the router guard, in every `history.push` and in
 * each form's back button, so adding a library meant finding all of them.
 *
 * The list page reads this for its tabs; the creation form reads it for its
 * type indicator and switcher. One definition, so a library cannot appear in
 * the tab strip and be missing from the switcher.
 */

export type TemplateLibraryId = 'structure' | 'branding' | 'table-columns' | 'playbooks';

export interface TemplateLibrary {
  id: TemplateLibraryId;
  /** i18n key - the library's name, as a tab and as a heading. */
  label: string;
  /** i18n key - what one of these templates IS, for the type indicator. */
  description: string;
  /** i18n key - the singular noun, for "New <noun>". */
  noun: string;
  /** Key into `react-icons/pi`, resolved by the caller that renders it. */
  icon: 'PiListLight' | 'PiTableLight' | 'PiBookOpenTextLight' | 'PiPaletteLight';
}

export const TEMPLATE_LIBRARIES: TemplateLibrary[] = [
  {
    id: 'structure',
    label: 'Document Structure',
    description: 'The sections and subsections a drafted document is built from.',
    noun: 'Structure Template',
    icon: 'PiListLight',
  },
  {
    id: 'branding',
    label: 'Branding',
    description: 'The fonts, margins, headers, and page layout applied to a drafted document.',
    noun: 'Branding Template',
    icon: 'PiPaletteLight',
  },
  {
    id: 'table-columns',
    label: 'Table Columns',
    description: 'A reusable set of columns to run across a batch of documents.',
    noun: 'Table Columns Template',
    icon: 'PiTableLight',
  },
  {
    id: 'playbooks',
    label: 'Playbooks',
    description: 'The clause rules and global rules a contract review checks against.',
    noun: 'Playbook',
    icon: 'PiBookOpenTextLight',
  },
];

/** The library for an id, or undefined when the id is not one of ours. */
export const templateLibrary = (
  id: string | null | undefined,
): TemplateLibrary | undefined => TEMPLATE_LIBRARIES.find((lib) => lib.id === id);

/** Where a library's list lives. */
export const templateLibraryPath = (id: TemplateLibraryId): string =>
  `/templates?type=${id}`;

/** Where a library's creation form lives. */
export const templateCreatePath = (id: TemplateLibraryId): string =>
  `/templates/add?type=${id}`;
